<script>
export default {
  name: "ProgramSessionFeesSummary",
};
</script>

<script setup>
import { defineProps, computed } from "vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import { convertCost } from "@/components/program-manager/sessions/utils";
import { APPLICATION_FEE_DATA } from "@/constants";
import { useCapitalizeWords } from "@/composables/stringManupulation";
import { toRefs } from "vue";

const props = defineProps({
  productPrices: {
    type: Object,
    required: false,
    default: () => ({
      applicationDeposit: undefined,
      confirmationPayment: undefined,
      educationalFee: 0,
      groupFlightFee: 0,
      publicTransportationFee: 0,
      housingFee: 0,
      classesFee: 0,
      internshipFee: 0,
      excursionFee: 0,
      totalBasePrice: 0,
      insuranceFee: 0,
    }),
  },
  isSessionOwner: {
    type: Boolean,
  },
});

const { productPrices } = toRefs(props);

const convertedApplicatonDeposit = computed(() => {
  return Number.isInteger(productPrices.value?.applicationDeposit)
    ? convertCost(productPrices.value?.applicationDeposit)
    : "N/A";
});

const convertedConfirmationPayment = computed(() => {
  return Number.isInteger(productPrices.value?.confirmationPayment)
    ? convertCost(productPrices.value?.confirmationPayment)
    : "N/A";
});
</script>

<template>
  <div>
    <ExpandingSection
      field-label="PROGRAM SESSION FEES SUMMARY"
      :expanded-by-default="true"
    >
      <template #content>
        <div class="grid grid-cols-1 gap-y-3">
          <div class="flex justify-between">
            <div class="common-label-text">
              {{ useCapitalizeWords(APPLICATION_FEE_DATA.label) }}
            </div>
            <div>{{ convertedApplicatonDeposit }}</div>
          </div>
          <div class="flex justify-between">
            <div class="common-label-text">Confirmation Payment</div>
            <div>
              {{ convertedConfirmationPayment }}
            </div>
          </div>
          <div class="flex justify-between">
            <div class="common-label-text">Group Flight Fee</div>
            <div>{{ convertCost(props.productPrices.groupFlightFee) }}</div>
          </div>
          <div class="flex justify-between">
            <div class="common-label-text">Public Transportation Fee</div>
            <div>
              {{ convertCost(props.productPrices.publicTransportationFee) }}
            </div>
          </div>
          <div class="flex justify-between">
            <div class="common-label-text">Medical Insurance</div>
            <div>
              {{ convertCost(props.productPrices.insuranceFee) }}
            </div>
          </div>
          <div class="flex justify-between">
            <div class="common-label-text">Housing Fee</div>
            <div>{{ convertCost(props.productPrices.housingFee) }}</div>
          </div>
          <div v-if="!props.isSessionOwner" class="flex justify-between">
            <div class="common-label-text">Educational Fee</div>
            <div>{{ convertCost(props.productPrices.educationalFee) }}</div>
          </div>
          <ExpandingSection
            v-else
            field-label-classes="common-label-text capitalize"
            data-testid="educational-fee-dropdown"
            disclosure-button-classes="w-full"
            input-classes="border-0 pt-3 pl-6 pr-0"
            :expanded-by-default="true"
          >
            <template #fieldLabel="{ open }">
              <div class="flex justify-between w-full mt-1 items-center">
                <div class="flex items-center">

                  <div class="common-label-text capitalize">
                    Educational Fee
                  </div>
                </div>
                <div class="font-normal text-base">
                  {{ convertCost(props.productPrices.educationalFee) }}
                </div>
              </div>
            </template>
            <template #content>
              <div class="grid grid-cols-1 gap-y-4">
                <div class="flex justify-between">
                  <div class="common-label-text">Classes Fee</div>
                  <div class="font-normal">
                    {{ convertCost(props.productPrices.classesFee) }}
                  </div>
                </div>
                <div class="flex justify-between">
                  <div class="common-label-text">Internship Fee</div>
                  <div>
                    {{ convertCost(props.productPrices.internshipFee) }}
                  </div>
                </div>
                <div class="flex justify-between">
                  <div class="common-label-text">Excursion Fee</div>
                  <div>{{ convertCost(props.productPrices.excursionFee) }}</div>
                </div>
              </div>
            </template>
          </ExpandingSection>

        </div>
        <hr class="pb-4 text-gray-300" />
        <div class="flex justify-between">
          <div class="block font-semibold text-gray-700">
            Program Session Base Price
          </div>
          <div>{{ convertCost(props.productPrices.totalBasePrice) }}</div>
        </div>
      </template>
    </ExpandingSection>
  </div>
</template>
